import React, { useState, useEffect } from 'react';
import { statsApi, languagesApi } from '../utils/api';
import { formatStrength } from '../utils/strengthUtils';
import { useI18n } from '../contexts/I18nContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import LanguageFlag from '../components/LanguageFlag';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart3,
  TrendingUp,
  BookMarked,
  Calendar,
  Target,
  Clock,
  RefreshCw,
  Eye,
  Languages,
  CheckCircle,
  AlertCircle,
  Trophy,
  Flame,
  Zap,
  Globe
} from 'lucide-react';

export default function Stats() {
  const { t } = useI18n();
  const [overview, setOverview] = useState(null);
  const [languageStats, setLanguageStats] = useState([]);
  const [activity, setActivity] = useState([]);
  const [srsHealth, setSrsHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('7');

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const [overviewRes, langRes, activityRes, srsRes] = await Promise.all([
          statsApi.getOverview(),
          statsApi.getLanguageStats(),
          statsApi.getActivity({ days: parseInt(days) }),
          statsApi.getSrsHealth(),
        ]);
        
        setOverview(overviewRes.data);
        setLanguageStats(langRes.data);
        setActivity(activityRes.data);
        setSrsHealth(srsRes.data);
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [days]);

  if (loading) {
    return <StatsSkeleton />;
  }

  // Calculate totals excluding ignored
  const totalActive = (overview?.total_vocabulary || 0) - (overview?.status_counts?.ignored || 0);
  const mastered = overview?.status_counts?.mastered || 0;
  const notMastered = totalActive - mastered;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('stats.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('stats.subtitle')}
          </p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40" data-testid="days-filter">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{t('stats.last7Days')}</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">{t('stats.last30Days')}</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={BookMarked}
          label="Active Vocabulary"
          value={totalActive}
          sublabel={`(${overview?.status_counts?.ignored || 0} ignored)`}
          iconColor="text-blue-500"
          bgColor="bg-blue-500/10"
        />
        <StatCard
          icon={Target}
          label="Mastered"
          value={mastered}
          iconColor="text-green-500"
          bgColor="bg-green-500/10"
        />
        <StatCard
          icon={Clock}
          label="Due Today"
          value={overview?.due_today || 0}
          iconColor="text-orange-500"
          bgColor="bg-orange-500/10"
        />
        <StatCard
          icon={TrendingUp}
          label="Avg Strength"
          value={formatStrength(overview?.average_strength || 0)}
          iconColor="text-purple-500"
          bgColor="bg-purple-500/10"
        />
      </div>

      {/* SRS Health */}
      <SrsHealthSection srsHealth={srsHealth} />

      {/* Language Mastery Leaderboard - Motivational Style */}
      <Card data-testid="languages-leaderboard-card" className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-pink-500/10">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Language Mastery Leaderboard
          </CardTitle>
          <CardDescription>
            Your progress across languages - keep climbing!
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {languageStats.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Flame className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Start learning to see your progress!</p>
            </div>
          ) : (
            <div className="divide-y">
              {languageStats
                .map(ls => {
                  // API returns known_count, familiar_count etc directly (not under status_counts)
                  const total = ls.total_unique_words || ls.total_vocabulary || 0;
                  const masteredCount = ls.mastered_count || 0;
                  const knownCount = ls.known_count || 0;
                  const familiarCount = ls.familiar_count || 0;
                  const learningCount = ls.learning_count || 0;
                  const newCount = ls.to_learn_count || 0; // to_learn_count is "new" words
                  const masteredPercent = total > 0 ? (masteredCount / total * 100) : 0;
                  
                  // Calculate XP-style score (weighted mastery)
                  const xpScore = (masteredCount * 100) + (knownCount * 70) + (familiarCount * 40) + (learningCount * 10);
                  
                  return { ...ls, total, masteredCount, knownCount, familiarCount, learningCount, newCount, masteredPercent, xpScore, ignored: 0 };
                })
                .sort((a, b) => b.xpScore - a.xpScore) // Sort by XP
                .map((ls, index) => {
                  const rankEmoji = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`;
                  const isTopRank = index < 3;
                  
                  return (
                    <div 
                      key={ls.language?.language_id} 
                      className={`p-4 transition-colors hover:bg-muted/50 ${isTopRank ? 'bg-gradient-to-r from-amber-500/5 to-transparent' : ''}`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Rank Badge */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold
                          ${index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-lg' : ''}
                          ${index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-white' : ''}
                          ${index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' : ''}
                          ${index > 2 ? 'bg-muted text-muted-foreground' : ''}
                        `}>
                          {rankEmoji}
                        </div>
                        
                        {/* Language Info */}
                        <div className="flex-grow min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {ls.language?.code && (
                              <LanguageFlag code={ls.language.code} size="md" />
                            )}
                            <h3 className="font-bold text-lg truncate">{ls.language?.name}</h3>
                            {ls.language?.native_name && ls.language.native_name !== ls.language.name && (
                              <span className="text-muted-foreground text-sm hidden sm:inline">
                                ({ls.language.native_name})
                              </span>
                            )}
                            {ls.language?.direction === 'rtl' && (
                              <Badge variant="outline" className="text-[10px] px-1">RTL</Badge>
                            )}
                          </div>
                          
                          {/* Mastery Level Breakdown - Horizontal Bars */}
                          <div className="flex h-3 rounded-full overflow-hidden bg-muted mb-2">
                            {ls.masteredCount > 0 && (
                              <div 
                                className="bg-foreground transition-all" 
                                style={{ width: `${(ls.masteredCount / ls.total) * 100}%` }}
                                title={`Mastered: ${ls.masteredCount}`}
                              />
                            )}
                            {ls.knownCount > 0 && (
                              <div 
                                className="bg-violet-500 transition-all" 
                                style={{ width: `${(ls.knownCount / ls.total) * 100}%` }}
                                title={`Known: ${ls.knownCount}`}
                              />
                            )}
                            {ls.familiarCount > 0 && (
                              <div 
                                className="bg-purple-500 transition-all" 
                                style={{ width: `${(ls.familiarCount / ls.total) * 100}%` }}
                                title={`Familiar: ${ls.familiarCount}`}
                              />
                            )}
                            {ls.learningCount > 0 && (
                              <div 
                                className="bg-orange-500 transition-all" 
                                style={{ width: `${(ls.learningCount / ls.total) * 100}%` }}
                                title={`Learning: ${ls.learningCount}`}
                              />
                            )}
                            {ls.newCount > 0 && (
                              <div 
                                className="bg-red-500 transition-all" 
                                style={{ width: `${(ls.newCount / ls.total) * 100}%` }}
                                title={`New: ${ls.newCount}`}
                              />
                            )}
                          </div>
                          
                          {/* Stats Row */}
                          <div className="flex flex-wrap gap-3 text-xs">
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-foreground" />
                              <strong>{ls.masteredCount}</strong> mastered
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-violet-500" />
                              <strong>{ls.knownCount}</strong> known
                            </span>
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <strong>{ls.learningCount + ls.newCount}</strong> in progress
                            </span>
                          </div>
                        </div>
                        
                        {/* XP Score */}
                        <div className="flex-shrink-0 text-right">
                          <div className="flex items-center gap-1 text-amber-500">
                            <Flame className="h-4 w-4" />
                            <span className="font-bold text-lg">{ls.xpScore.toLocaleString()}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">XP</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
          
          {/* Legend */}
          {languageStats.length > 0 && (
            <div className="p-4 bg-muted/30 border-t">
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground justify-center">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-foreground" /> Mastered</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" /> Known</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500" /> Familiar</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" /> Learning</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> New</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Known Words per Language Bar Chart */}
      <KnownWordsChart languageStats={languageStats} />

      {/* Frequency Progress Tracker */}
      <FrequencyProgressTracker languageStats={languageStats} />

      {/* Activity & Language Progress */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Activity Chart */}
        <Card data-testid="activity-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activity.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No activity in this period
              </div>
            ) : (
              <div className="space-y-3">
                {activity.slice(-7).map((day) => (
                  <div key={day.date} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {new Date(day.date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {day.exposures}
                        </span>
                        <span className="flex items-center gap-1">
                          <RefreshCw className="h-3 w-3" />
                          {day.reviews}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1 h-4">
                      <div 
                        className="bg-blue-500 rounded-sm transition-all"
                        style={{ width: `${Math.min(day.exposures, 100)}%` }}
                        title={`${day.exposures} exposures`}
                      />
                      <div 
                        className="bg-green-500 rounded-sm transition-all"
                        style={{ width: `${Math.min(day.reviews * 2, 50)}%` }}
                        title={`${day.reviews} reviews`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex gap-4 mt-4 pt-4 border-t text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-blue-500" />
                Exposures
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-green-500" />
                Reviews
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card data-testid="status-distribution-card">
          <CardHeader>
            <CardTitle>Status Distribution</CardTitle>
            <CardDescription>
              Breakdown by learning stage (ignored excluded)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <StatusDistribution
                label="New"
                count={overview?.status_counts?.new || 0}
                total={totalActive || 1}
                color="bg-red-500"
              />
              <StatusDistribution
                label="Learning"
                count={overview?.status_counts?.learning || 0}
                total={totalActive || 1}
                color="bg-orange-500"
              />
              <StatusDistribution
                label="Familiar"
                count={overview?.status_counts?.familiar || 0}
                total={totalActive || 1}
                color="bg-purple-500"
              />
              <StatusDistribution
                label="Known"
                count={overview?.status_counts?.known || 0}
                total={totalActive || 1}
                color="bg-violet-500"
              />
              <StatusDistribution
                label="Mastered"
                count={overview?.status_counts?.mastered || 0}
                total={totalActive || 1}
                color="bg-foreground"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Summary */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/10">
                <Eye className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-3xl font-bold">{overview?.recent_exposures_7d || 0}</p>
                <p className="text-sm text-muted-foreground">Exposures (7 days)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10">
                <RefreshCw className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-3xl font-bold">{overview?.recent_reviews_7d || 0}</p>
                <p className="text-sm text-muted-foreground">Reviews (7 days)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-500/10">
                <BookMarked className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <p className="text-3xl font-bold">{overview?.total_texts || 0}</p>
                <p className="text-sm text-muted-foreground">Texts Imported</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sublabel, iconColor, bgColor }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${bgColor}`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-muted-foreground">{label}</p>
            {sublabel && (
              <p className="text-xs text-muted-foreground">{sublabel}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusDistribution({ label, count, total, color }) {
  const percentage = total > 0 ? (count / total * 100) : 0;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm text-muted-foreground">{count}</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div 
          className={`h-full ${color} transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground text-right">
        {percentage.toFixed(1)}%
      </p>
    </div>
  );
}

function SrsHealthSection({ srsHealth }) {
  if (!srsHealth) return null;

  const { accuracy_7d, accuracy_30d, total_reviews_7d, overdue_count, forecast, streak_days } = srsHealth;
  const maxForecast = Math.max(...(forecast || []).map(d => d.count), 1);

  const accuracyColor = (pct) => {
    if (pct === null || pct === undefined) return 'text-muted-foreground';
    if (pct >= 85) return 'text-green-500';
    if (pct >= 70) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <Card data-testid="srs-health-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-green-500" />
          Review Health
        </CardTitle>
        <CardDescription>Accuracy, forecast, and streak from your spaced repetition practice</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {/* Accuracy 7d */}
          <div className="rounded-lg bg-muted/50 p-3 text-center">
            <p className={`text-2xl font-bold ${accuracyColor(accuracy_7d)}`}>
              {accuracy_7d !== null && accuracy_7d !== undefined ? `${accuracy_7d}%` : '—'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Accuracy (7d)</p>
            <p className="text-xs text-muted-foreground">{total_reviews_7d} reviews</p>
          </div>
          {/* Accuracy 30d */}
          <div className="rounded-lg bg-muted/50 p-3 text-center">
            <p className={`text-2xl font-bold ${accuracyColor(accuracy_30d)}`}>
              {accuracy_30d !== null && accuracy_30d !== undefined ? `${accuracy_30d}%` : '—'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Accuracy (30d)</p>
          </div>
          {/* Overdue */}
          <div className="rounded-lg bg-muted/50 p-3 text-center">
            <p className={`text-2xl font-bold ${overdue_count > 50 ? 'text-red-500' : overdue_count > 20 ? 'text-yellow-500' : 'text-green-500'}`}>
              {(overdue_count || 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Overdue</p>
          </div>
          {/* Streak */}
          <div className="rounded-lg bg-muted/50 p-3 text-center">
            <p className="text-2xl font-bold text-amber-500">{streak_days || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Day streak</p>
          </div>
        </div>

        {/* 7-day forecast */}
        {forecast && forecast.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">7-Day Review Forecast</p>
            <div className="flex items-end gap-2 h-24">
              {forecast.map((day) => {
                const height = maxForecast > 0 ? (day.count / maxForecast) * 100 : 0;
                return (
                  <div key={day.date} className="flex flex-col items-center flex-1 min-w-0">
                    <span className="text-[10px] text-muted-foreground mb-1 tabular-nums">{day.count > 0 ? day.count : ''}</span>
                    <div className="w-full flex-1 flex items-end">
                      <div
                        className="w-full rounded-t-sm bg-violet-500/70 transition-all duration-300"
                        style={{ height: `${Math.max(height, day.count > 0 ? 8 : 2)}%` }}
                        title={`${day.label}: ${day.count} due`}
                      />
                    </div>
                    <span className="text-[10px] text-muted-foreground mt-1">{day.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KnownWordsChart({ languageStats }) {
  if (!languageStats || languageStats.length === 0) return null;

  const data = languageStats
    .map(ls => ({
      name: ls.language?.name || ls.language?.code || '?',
      code: ls.language?.code || '',
      mastered: ls.mastered_count || 0,
      known: ls.known_count || 0,
      familiar: ls.familiar_count || 0,
    }))
    .filter(d => d.mastered + d.known + d.familiar > 0)
    .sort((a, b) => (b.mastered + b.known + b.familiar) - (a.mastered + a.known + a.familiar));

  if (data.length === 0) return null;

  const maxVal = Math.max(...data.map(d => d.mastered + d.known + d.familiar)) || 1;

  return (
    <Card data-testid="known-words-chart-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-violet-500" />
          Known Words per Language
        </CardTitle>
        <CardDescription>
          Mastered + known + familiar words by language
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {data.map(d => {
            const total = d.mastered + d.known + d.familiar;
            const pct = (total / maxVal) * 100;
            return (
              <div key={d.code} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 font-medium">
                    <LanguageFlag code={d.code} size="sm" />
                    {d.name}
                  </span>
                  <span className="text-muted-foreground tabular-nums">
                    <strong className="text-foreground">{total.toLocaleString()}</strong>
                    {' words'}
                  </span>
                </div>
                <div className="h-5 rounded-md bg-muted overflow-hidden flex">
                  {d.mastered > 0 && (
                    <div
                      className="bg-foreground h-full transition-all duration-500"
                      style={{ width: `${(d.mastered / maxVal) * 100}%` }}
                      title={`Mastered: ${d.mastered}`}
                    />
                  )}
                  {d.known > 0 && (
                    <div
                      className="bg-violet-500 h-full transition-all duration-500"
                      style={{ width: `${(d.known / maxVal) * 100}%` }}
                      title={`Known: ${d.known}`}
                    />
                  )}
                  {d.familiar > 0 && (
                    <div
                      className="bg-purple-500/70 h-full transition-all duration-500"
                      style={{ width: `${(d.familiar / maxVal) * 100}%` }}
                      title={`Familiar: ${d.familiar}`}
                    />
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span><span className="inline-block w-2 h-2 rounded-sm bg-foreground mr-1" />{d.mastered.toLocaleString()} mastered</span>
                  <span><span className="inline-block w-2 h-2 rounded-sm bg-violet-500 mr-1" />{d.known.toLocaleString()} known</span>
                  <span><span className="inline-block w-2 h-2 rounded-sm bg-purple-500/70 mr-1" />{d.familiar.toLocaleString()} familiar</span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function StatsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-4 w-48 mt-2" />
        </div>
        <Skeleton className="h-10 w-40" />
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <Card key={i}>
            <CardContent className="pt-6">
              <Skeleton className="h-12 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2].map(i => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// Frequency Progress Tracker Component
function FrequencyProgressTracker({ languageStats }) {
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [frequencyData, setFrequencyData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Get available languages from user's language stats
  const availableLanguages = languageStats
    .filter(ls => ls.language?.code)
    .map(ls => ({
      code: ls.language.code,
      name: ls.language.name,
      languageId: ls.language.language_id
    }));

  // Auto-select first language if none selected
  useEffect(() => {
    if (!selectedLanguage && availableLanguages.length > 0) {
      setSelectedLanguage(availableLanguages[0].code);
    }
  }, [availableLanguages, selectedLanguage]);

  // Fetch frequency data when language changes
  useEffect(() => {
    if (!selectedLanguage) return;

    const fetchFrequencyData = async () => {
      setLoading(true);
      try {
        const response = await statsApi.getFrequencyProgress(selectedLanguage);
        setFrequencyData(response.data);
      } catch (err) {
        console.error('Error fetching frequency data:', err);
        setFrequencyData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFrequencyData();
  }, [selectedLanguage]);

  const bandColors = {
    top_1k: 'bg-emerald-500',
    '1k_2k': 'bg-green-500',
    '2k_3k': 'bg-lime-500',
    '3k_5k': 'bg-yellow-500',
    '5k_plus': 'bg-orange-500'
  };

  const bandOrder = ['top_1k', '1k_2k', '2k_3k', '3k_5k', '5k_plus'];

  // Combine bands 5k-10k and 10k+ into 5k_plus for display
  const processedData = frequencyData ? {
    ...frequencyData,
    bands: frequencyData.bands ? {
      ...frequencyData.bands,
      '5k_plus': {
        known: (frequencyData.bands['5k_10k']?.known || 0) + (frequencyData.bands['10k_plus']?.known || 0),
        total: (frequencyData.bands['5k_10k']?.total || 0) + (frequencyData.bands['10k_plus']?.total || 0),
        percentage: (() => {
          const total = (frequencyData.bands['5k_10k']?.total || 0) + (frequencyData.bands['10k_plus']?.total || 0);
          const known = (frequencyData.bands['5k_10k']?.known || 0) + (frequencyData.bands['10k_plus']?.known || 0);
          return total > 0 ? Math.round(known / total * 100 * 10) / 10 : 0;
        })()
      }
    } : {},
    band_labels: {
      ...frequencyData.band_labels,
      '5k_plus': '5,001 - 50,000'
    },
    // Calculate top 5k progress
    top5k_known: (frequencyData.bands?.top_1k?.known || 0) + 
                 (frequencyData.bands?.['1k_2k']?.known || 0) + 
                 (frequencyData.bands?.['2k_3k']?.known || 0) + 
                 (frequencyData.bands?.['3k_5k']?.known || 0),
    top5k_total: 5000,
    top5k_percentage: (() => {
      const known = (frequencyData.bands?.top_1k?.known || 0) + 
                   (frequencyData.bands?.['1k_2k']?.known || 0) + 
                   (frequencyData.bands?.['2k_3k']?.known || 0) + 
                   (frequencyData.bands?.['3k_5k']?.known || 0);
      return Math.round(known / 5000 * 100 * 10) / 10;
    })()
  } : null;

  if (availableLanguages.length === 0) {
    return null; // Don't show if no languages
  }

  return (
    <Card data-testid="frequency-progress-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              Frequency Progress
            </CardTitle>
            <CardDescription>
              Track your vocabulary against the most common words
            </CardDescription>
          </div>
          <Select value={selectedLanguage || ''} onValueChange={setSelectedLanguage}>
            <SelectTrigger className="w-48" data-testid="frequency-language-select">
              <Globe className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              {availableLanguages.map(lang => (
                <SelectItem key={lang.code} value={lang.code}>
                  <span className="flex items-center gap-2">
                    <LanguageFlag code={lang.code} size="sm" />
                    {lang.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : !frequencyData?.has_frequency_data ? (
          <div className="text-center py-8 text-muted-foreground">
            <Globe className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No frequency data available for this language</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overall Progress - Top 5000 words */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Top 5,000 Words</span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {frequencyData.estimated_level?.toUpperCase()}
                  </Badge>
                  <span className="text-lg font-bold">
                    {processedData?.top5k_known?.toLocaleString()} / 5,000
                  </span>
                </div>
              </div>
              <Progress value={processedData?.top5k_percentage || 0} className="h-3" />
              <p className="text-xs text-muted-foreground mt-1 text-right">
                {processedData?.top5k_percentage?.toFixed(1)}% of top 5,000 words known
              </p>
            </div>

            {/* Band Breakdown */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Frequency Bands</h4>
              {bandOrder.map(bandKey => {
                const band = processedData?.bands?.[bandKey];
                const label = processedData?.band_labels?.[bandKey] || bandKey;
                if (!band) return null;

                const percentage = band.percentage || 0;
                
                return (
                  <div key={bandKey} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded ${bandColors[bandKey]}`} />
                        {label}
                      </span>
                      <span className="text-muted-foreground">
                        <strong className="text-foreground">{band.known?.toLocaleString()}</strong>
                        {' / '}{band.total?.toLocaleString()}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div 
                        className={`h-full ${bandColors[bandKey]} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tip */}
            <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
              <strong>Tip:</strong> Focus on the top frequency bands first. Knowing the top 3,000 words 
              covers ~90% of everyday conversations.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
